import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MarkdownModule } from 'ngx-markdown';
import { QuillEditorComponent } from 'ngx-quill';
import { NotEnoughCreditComponent } from 'src/app/shared/components/snackbar/not-enough-credit/not-enough-credit.component';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ContentService } from '../../content.service';

@Component({
  selector: 'app-search-chat',
  template: `
    <div class="flex flex-col h-full bg-white relative">
      <!-- Header -->
      <header class="flex items-center justify-between p-2 border-b border-[#e0e0e0] bg-white z-10 w-full">
        <div class="flex items-center gap-2 min-w-0">
          <button mat-icon-button (click)="back.emit()" class="text-[#5f6368] flex-shrink-0">
            <mat-icon>arrow_back</mat-icon>
          </button>
          <div class="flex flex-col min-w-0">
            <h3 class="margin-0 text-[14px] font-bold text-[#202124] truncate pr-2">
              {{ session().query }}
            </h3>
            <span class="text-[10px] text-[#5f6368]">
              {{ 'Search.ChatTitle' | translate }}
            </span>
          </div>
        </div>
        <button mat-icon-button (click)="close.emit()" class="text-[#5f6368] flex-shrink-0">
          <mat-icon>close</mat-icon>
        </button>
      </header>

      <!-- Chat Area -->
      <div class="flex-1 overflow-y-auto p-4 custom-scrollbar flex flex-col" #scrollContainer>


        <!-- Message History -->
        <div class="flex flex-col gap-5 flex-1">
          @for (msg of messages(); track $index) {
            <div class="flex flex-col max-w-[85%]" [class.self-end]="msg.sender === 'user'" [class.self-start]="msg.sender === 'assistant'">
              @if (msg.sender === 'user') {
                <div class="bg-[#f5f8ff] text-[#1e1e1e] rounded-[18px_18px_4px_18px] px-4 py-3 text-sm whitespace-pre-wrap">
                  {{ msg.text }}
                </div>
              } @else {
                <div class="text-[#1e1e1e] p-0 pr-4 text-sm w-full bubble-content">
                  <markdown [data]="msg.text"></markdown>
                </div>
              }
            </div>
          }

          @if (loading()) {
            <div class="flex items-center gap-2 text-sm text-[#5f6368] self-start p-2">
              <mat-spinner diameter="18"></mat-spinner>
              <span>{{ 'Search.AiThinking' | translate }}</span>
            </div>
          }
        </div>
      </div>

      <!-- Input Area -->
      <div class="input-area">
        <div class="input-container">
          <quill-editor
            class="chat-input-editor"
            [(ngModel)]="newMessageText"
            [modules]="quillModules"
            [format]="'text'"
            [readOnly]="loading()"
            [placeholder]="'Search.ChatPlaceholder' | translate"
            (keydown)="onEditorKeyDown($event)"
          ></quill-editor>

          <div class="input-actions">
            <div class="spacer"></div>
            <button mat-fab color="primary" class="send-btn" (click)="sendMessage()" [disabled]="loading() || !newMessageText?.trim()">
              <mat-icon>send</mat-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: `
    :host {
      display: block;
      height: 100%;
      width: 100%;
      overflow: hidden;
    }
    .bubble-content {
      ::ng-deep {
        p {
          margin-top: 0;
          margin-bottom: 0.5rem;
          line-height: 1.5;
        }
        h1,
        h2,
        h3,
        h4 {
          margin-top: 0.75rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        ul,
        ol {
          margin-top: 0;
          margin-bottom: 0.5rem;
          padding-left: 1.25rem;
        }
        li {
          margin-bottom: 0.25rem;
        }
      }
    }
    .input-area {
      padding: 16px 24px 24px;
      background-color: #fff;
      position: relative;
      border-top: 1px solid #f0f0f0;

      .input-container {
        background-color: #f8f9fa;
        border-radius: 24px;
        padding: 8px 8px 8px 16px;
        display: flex;
        flex-direction: column;
        border: 1px solid transparent;
        transition: all 0.2s ease;
        position: relative;
        min-height: 56px;
      }

      .chat-input-editor {
        display: block;
        width: 100%;

        ::ng-deep .ql-container.ql-snow {
          border: none;
          font-family: inherit;
        }

        ::ng-deep .ql-editor {
          padding: 8px 0;
          min-height: 24px;
          max-height: 150px;
          overflow-y: auto;
          font-size: 15px;
          line-height: 1.5;

          &.ql-blank::before {
            font-style: normal;
            color: #757575;
            left: 0;
          }
        }
      }

      .input-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 4px;

        .spacer {
          flex-grow: 1;
        }

        .send-btn {
          box-shadow: none;
          width: 40px;
          height: 40px;
          min-width: 40px;

          background-color: var(--skz-background-content-button) !important;
          color: #fff !important;

          ::ng-deep .mat-mdc-fab-touch-target {
            height: 40px;
            width: 40px;
          }

          &:disabled {
            background-color: #e0e0e0;
            color: #9e9e9e;
          }
        }
      }
    }
  `,
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, TranslateModule, MarkdownModule, QuillEditorComponent],
})
export class SearchChatComponent implements OnInit {
  session = input.required<any>();
  back = output<void>();
  close = output<void>();
  sessionUpdated = output<any>();

  messages = signal<any[]>([]);
  newMessageText = '';
  loading = signal(false);
  quillModules = { toolbar: false };

  private contentService = inject(ContentService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);

  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;

  ngOnInit() {
    this.initializeMessages();
  }

  private initializeMessages() {
    const s = this.session();
    let msgs = s.messages || [];
    if (msgs.length === 0 && s.AIResponse) {
      msgs = [
        { sender: 'user', text: s.query, createdAt: s.createdAt },
        { sender: 'assistant', text: s.AIResponse, createdAt: s.createdAt },
      ];
    }
    this.messages.set(msgs);
    this.scrollToBottom();
  }

  onEditorKeyDown(event: KeyboardEvent): void {
    if (this.loading()) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  sendMessage() {
    const text = this.newMessageText.trim();
    if (!text || this.loading()) return;

    this.newMessageText = '';
    this.loading.set(true);

    // Pre-append user message locally for immediate UI update
    this.messages.update((prev) => [...prev, { sender: 'user', text, createdAt: new Date() }]);
    this.scrollToBottom();

    this.contentService.addMessageToSearchSession(this.session()._id, text).subscribe({
      next: (searchDoc: any) => {
        this.loading.set(false);
        if (searchDoc) {
          this.messages.set(searchDoc.messages || []);
          this.sessionUpdated.emit(searchDoc);
        }
        this.scrollToBottom();
      },
      error: (err) => {
        this.handleError(err);
      },
    });
  }

  private handleError(err: any) {
    this.loading.set(false);

    const msg = err && err.message ? err.message : '';
    if (msg === 'notEnoughCredits') {
      this.toast.snackbar.openFromComponent(NotEnoughCreditComponent, {
        data: {
          message: this.translate.instant('Toastr.notEnoughCredits'),
          linkText: this.translate.instant('Toastr.addCreditsHere'),
          linkUrl: '/myspace/credit',
        },
        duration: 10000,
      });
    } else {
      this.toast.showWithoutHeader({ level: 'error', text: this.translate.instant('Global.Error') });
    }
  }

  private scrollToBottom() {
    setTimeout(() => {
      if (this.scrollContainer) {
        this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
      }
    }, 100);
  }
}
